import chroma from 'chroma-js';
import { type TextObjectV2 as MapTextObject, TRANSPARENT } from '../../../../common/types';
import BBoxObject from '../bbox/BBoxObject';
import CanvasObject from '../CanvasObject';
import { opacityWhileDragging } from '../common';
import * as geometry from '../common/geometry';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import { getFontSizeToFitForStrings, TEXT_PADDING } from './common';

export default class EditTextObject extends CanvasObject {
  text: MapTextObject;
  box: Point[];
  refBox: Point[];
  vertices: LngLat[] = [];
  bbox: BBoxObject;
  input?: HTMLDivElement;
  description: string[];
  fontSize = 0;
  isDragging: boolean = false;

  _onTextUpdated: (p: LngLat[], description: string) => void;

  constructor(
    id: string,
    surface: SurfaceBase,
    text: MapTextObject,
    onTextUpdated: (p: LngLat[], description: string) => void,
  ) {
    super(id, surface);
    this.text = text;
    this.description = text.description.split('\n');
    this._onTextUpdated = onTextUpdated;
    this.interactive = true;

    this.refBox = this.box = text.geometry.coordinates[0].slice(0, -1).map(([lng, lat]) => {
      this.vertices.push({ lng, lat });
      return this.surface.toScreenCoords({ lng, lat });
    });

    this.bbox = new BBoxObject(`bbox-${id}`, surface, this);
    this.addChild(this.bbox);

    this.on('scaleStart', () => {
      this.refBox = this.bbox.getBBoxCoords();
    });

    this.on('scaling', () => {
      this.box = this.bbox.getBBoxCoords();
      this.renderAll();
    });

    this.on('scaleEnd', () => {
      this.box = this.bbox.getBBoxCoords();
      this.refBox = this.box;
      this.onTextUpdated();
      this.renderAll();
    });

    this.on('rotateStart', () => {
      this.refBox = this.box;
    });

    this.on('rotating', (e) => {
      this.box = geometry.polygonRotate(this.refBox, e.angle, geometry.polygonCentroid(this.refBox));

      this.renderAll();
    });

    this.on('rotateEnd', () => {
      this.onTextUpdated();
    });

    this.on('dragStart', (e) => {
      this.refBox = this.box;
      this.isDragging = true;
      this.bbox.emit('dragStart', e);
    });

    this.on('dragging', (e) => {
      const { x, y } = e.diff();
      this.box = this.refBox.map((c) => ({
        x: c.x + x,
        y: c.y + y,
      }));
      this.bbox.emit('dragging', e);
      this.renderAll();
    });
    this.on('dragEnd', (e) => {
      this.isDragging = false;
      this.onTextUpdated();
      this.bbox.emit('dragEnd', e);
      this.renderAll();
    });

    this.on('tap', () => {
      const borderWidth = 1;
      this.surface.disableInteractionEvents();
      const [p1, p2] = this.box;
      const startAngle = geometry.lineAngle(p1, p2);
      const maxWidth = geometry.distance(p1, p2) - 2 * TEXT_PADDING;
      this.input = window.document.createElement('div');
      this.input.setAttribute('contenteditable', 'true');
      this.input.innerText = this.description.join('\n');
      this.input.style.position = 'absolute';
      this.input.style.top = `${p1.y - 2 * borderWidth}px`;
      this.input.style.left = `${p1.x - 2 * borderWidth}px`;
      this.input.style.whiteSpace = 'pre';
      this.input.style.border = `${borderWidth}px solid red`;
      this.input.style.padding = `${TEXT_PADDING}px`;
      this.input.style.transformOrigin = `0 0`;
      this.input.style.transform = `rotate(${startAngle}rad)`;

      const fontSize = getFontSizeToFitForStrings(this.description, maxWidth, this.surface.context);
      this.input.style.fontSize = `${fontSize}px`;
      this.input.style.lineHeight = `${fontSize}px`;
      this.input.style.fontFamily = 'arial';
      this.input.style.color = text.properties.color ?? '#000000';
      if (text.properties.backgroundColor !== TRANSPARENT) {
        this.input.style.backgroundColor = chroma(text.properties.backgroundColor).alpha(0.75).hex();
      }

      this.input.addEventListener('blur', () => {
        const text = this.filterInputLineBreaks(this.input!.childNodes);

        const newWidth = this.input!.clientWidth + TEXT_PADDING - borderWidth;
        const newHeight = this.input!.clientHeight + borderWidth;

        this.box = geometry.polygonRotate(
          [
            p1,
            { x: p1.x + newWidth, y: p1.y },
            { x: p1.x + newWidth, y: p1.y + newHeight },
            { x: p1.x, y: p1.y + newHeight },
          ],
          startAngle,
          p1,
        );

        this.description = text.split('\n');

        this.input!.remove();
        this.input = undefined;

        this.bbox = new BBoxObject(`bbox-${id}`, surface, this);
        this.onTextUpdated();
        this.addChild(this.bbox);
        this.renderAll();
        this.surface.enableInteractionEvents();
      });

      this.removeChild(this.bbox);
      this.renderAll();
      this.surface.canvas.parentElement!.appendChild(this.input);
      this.input.focus();
    });
  }

  // inspired by: https://stephenhaney.com/2020/get-contenteditable-plaintext-with-correct-linebreaks/
  filterInputLineBreaks = (childNodes: NodeList) => {
    let text: string = '';
    let isOnNewLine: boolean = true;

    function filterNodes(nodes: NodeList) {
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];

        // if nodeName is <br />, its on its own new line
        if (n.nodeName === 'BR') {
          text += '\n';
          isOnNewLine = true;
          continue;
        }

        // if the nodeName is a <div />, it creates a new line by itself
        if (n.nodeName === 'DIV' && isOnNewLine === false) {
          text += '\n';
        }

        isOnNewLine = false;

        // if the node is a text node (nodeName === '#text' / nodeType === 3)
        // and contains text content, add content to text.
        if (n.nodeType === 3 && n.textContent) {
          text += n.textContent;
        }

        // recursively call until all child nodes have been accounted for.
        filterNodes(n.childNodes);
      }
    }

    filterNodes(childNodes);

    return text;
  };

  onTextUpdated() {
    const coords = this.box.map((p) => this.surface.toMapCoords(p));
    this.vertices = coords;
    this._onTextUpdated([...coords, coords[0]], this.description.join('\n'));
  }

  getBBoxCoords = (): Point[] => {
    return this.box;
  };

  getCenter = (): Point => {
    return geometry.polygonCentroid(this.box);
  };

  refreshScreenCoords(): void {
    super.refreshScreenCoords();
    this.box = this.vertices.map((v) => this.surface.toScreenCoords(v));
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D, color: string): void {
    ctx.beginPath();
    ctx.moveTo(this.box[0].x, this.box[0].y);
    ctx.lineTo(this.box[1].x, this.box[1].y);
    ctx.lineTo(this.box[2].x, this.box[2].y);
    ctx.lineTo(this.box[3].x, this.box[3].y);
    ctx.fillStyle = color;
    ctx.fill();
  }

  getCurrentTextObject() {
    const currentObject = this.getStore().currentObject;
    if (currentObject?.type !== 'textV2') return;
    return currentObject;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const currentText = this.getCurrentTextObject();
    if (this.input || !currentText) {
      return;
    }
    const { properties } = currentText;
    const [p1, p2, p3, p4] = this.box;
    const center = this.getCenter();
    const angle = geometry.lineAngle(p1, p2);

    // reset global transparency to be 1 (fully opaque)
    ctx.globalAlpha = 1;

    // if dragging, need to set the transparency regardless because we do not
    // allow changing of text box transparency at the moment.

    if (this.isDragging) {
      ctx.globalAlpha = opacityWhileDragging;
    }

    const path = new Path2D();
    path.moveTo(p1.x, p1.y);
    path.lineTo(p2.x, p2.y);
    path.lineTo(p3.x, p3.y);
    path.lineTo(p4.x, p4.y);
    path.closePath();

    if (properties.backgroundColor !== TRANSPARENT) {
      ctx.save();
      ctx.fillStyle = chroma(properties.backgroundColor).alpha(0.75).hex();
      ctx.fill(path);
      ctx.restore();
    }

    if (properties.borderColor && properties.borderColor !== TRANSPARENT) {
      ctx.save();
      ctx.strokeStyle = properties.borderColor;
      ctx.lineWidth = 2;
      ctx.stroke(path);
      ctx.restore();
    }

    // in the text rendering below, 5px padding is added to the left side,
    // adding another 2 * 5px evens the padding, and gives it more of a buffer.
    const maxWidth = geometry.distance(p1, p2) - 2 * TEXT_PADDING;
    const height = geometry.distance(p2, p3) - 2 * TEXT_PADDING;
    const lines = this.description;
    const fontSize = getFontSizeToFitForStrings(lines, maxWidth, ctx);

    ctx.font = `${fontSize}px Inter`;
    ctx.textAlign = 'start';
    ctx.textBaseline = 'top';

    ctx.save();
    ctx.translate(center.x, center.y);
    ctx.rotate(angle);
    ctx.translate(-center.x, -center.y);

    ctx.fillStyle = properties.color;
    ctx.beginPath();
    for (let i = 0; i < lines.length; i++) {
      ctx.fillText(lines[i], center.x - maxWidth / 2, center.y - height / 2 + fontSize * i);
    }
    ctx.restore();
    this.surface.resetCanvasTransform(ctx);
  }
}
